import React, { useEffect, useState } from "react";
import { Select, Table, message, Empty } from "antd";
import { Link } from "react-router-dom";
import "../assets/css/paySlips.css";
import { companyID } from "../auth";
import { calculateSalaryBreakdown } from "../utils/calculateSalaryBreakdown";
import { formatDecimal } from "../utils/formatDecimal";
import { saveAs } from "file-saver";
import { convertDateFormate } from "../utils/convertDateFormate";
import api from "../api/axios";
import {
  getCompanySalaryConfig,
  getSalaryData,
} from "../services/salaryServices";

const { Option } = Select;

function PaySlips({ salaryConfig, employeeId }) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [financialYear, setFinancialYear] = useState(currentYear);
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeSalary, setEmployeeSalary] = useState(0);
  const companyId = companyID();
  const [salaryRecords, setSalaryRecords] = useState([]);

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (text) => {
        const monthYear = convertDateFormate(text);
        return new Date(monthYear.year, monthYear.month - 1).toLocaleString(
          "default",
          { month: "long" }
        );
      },
    },
    {
      title: "Total Salary",
      dataIndex: "total_salary",
      key: "total_salary",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Reimbursement",
      dataIndex: "reimbursement_allowance",
      key: "reimbursement_allowance",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Other deduction",
      dataIndex: "other_deduction",
      key: "other_deduction",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Total Deduction",
      dataIndex: "total_deduction",
      key: "total_deduction",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Net Salary",
      dataIndex: "net_salary",
      key: "net_salary",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Action",
      dataIndex: "salary_slip",
      key: "payslip",
      render: (text, record) => {
        return (
          <Link
            to="#"
            className="btn btn-sm btn-primary"
            onClick={(e) => {
              e.preventDefault();
              setLoading(true);
              api
                .get(`${text}`, {
                  responseType: "blob",
                })
                .then((res) => {
                  const pdfBlob = new Blob([res.data], {
                    type: "application/pdf",
                  });
                  const pdfUrl = URL.createObjectURL(pdfBlob);
                  saveAs(pdfBlob, text);
                })
                .catch((err) => {
                  console.error(err);
                })
                .finally(() => {
                  setLoading(false);
                });
            }}
          >
            View Slip
          </Link>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    api
      .get(`salary/${employeeId}/${financialYear}`)
      .then((response) => {
        setSalaryData(response.data.data);
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          setSalaryData([]);
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [financialYear]);

  const fetchCompanySalaryConfig = async (salary) => {
    try {
      const { config } = await getCompanySalaryConfig(companyId);
      const filteredData = await getSalaryData(salary, config);

      setSalaryRecords(
        filteredData.filter((salary) => salary.salary_status === 1)
      );
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    if (salaryData.length) {
      fetchCompanySalaryConfig(salaryData);
    }
  }, [salaryData]);

  const handleFinancialYearChange = (value) => {
    setFinancialYear(value);
  };

  const startYear = 2000;

  const financialYears = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year = startYear + i;
      return `${year}`;
    }
  );

  return (
    <div className="pay-slips">
      <h2>Pay slips for {financialYear}</h2>
      <div className="financialYearDropdown mb-2">
        <label>Select Financial Year:</label>
        <Select
          value={financialYear}
          style={{ width: 120 }}
          onChange={handleFinancialYearChange}
        >
          {financialYears.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
      <div className="row">
        <div className="table-responsive">
          {salaryRecords.length > 0 ? (
            <Table
              dataSource={salaryRecords}
              columns={columns}
              rowKey="month"
              loading={loading}
              pagination={false}
            />
          ) : !loading ? (
            <Empty description="No salary details available." />
          ) : (
            <Empty description="Fetching salary details..." />
          )}
        </div>
      </div>
    </div>
  );
}

export default PaySlips;
